import clsx from 'clsx';
import React, { useState } from 'react';
import { FiHome, FiUserPlus, FiUsers, FiBell, FiSettings, FiLogOut, FiLock } from 'react-icons/fi';
import { NavLink, useNavigate } from 'react-router-dom';

import Logo from '@/assets/images/baita-bank-logo.svg';
import { LogoutModal } from '@/design-system/components/molecules/LogoutModal';
import { useLogout } from '@/features/authentication/hooks/useAuth';
import { NotificationPanel } from '@/features/onboarding/components/NotificationPanel';

export interface TopBarProps {
  className?: string;
}

interface TabItem {
  label: string;
  path: string;
  icon: React.ReactNode;
  disabled?: boolean;
}

const tabs: TabItem[] = [
  { label: 'Início', path: '/modules', icon: <FiHome className="w-5 h-5" /> },
  { label: 'Onboarding', path: '/onboarding', icon: <FiUserPlus className="w-5 h-5" /> },
  {
    label: 'Gestão de acessos',
    path: '/access-management',
    icon: <FiUsers className="w-5 h-5" />,
    disabled: true,
  },
];

export const TopBar: React.FC<TopBarProps> = ({ className }) => {
  const navigate = useNavigate();
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const logoutMutation = useLogout();

  const handleLogoutClick = () => {
    setIsLogoutModalOpen(true);
  };

  const handleLogoutConfirm = () => {
    logoutMutation.mutate();
  };

  const handleLogoutModalClose = () => {
    if (!logoutMutation.isPending) {
      setIsLogoutModalOpen(false);
    }
  };

  return (
    <header className={clsx('bg-white shadow-sm', className)}>
      {/* Top Section - Logo e Ícones */}
      <div className="border-b border-neutral-200">
        <div className="max-w-screen-2xl mx-auto px-8 py-4">
          <div className="flex items-center">
            {/* Espaçador esquerdo para centralizar o logo */}
            <div className="flex-1"></div>

            {/* Logo centralizado */}
            <div className="flex justify-center">
              <img src={Logo} alt="Baita Bank" className="h-12" />
            </div>

            {/* Action Icons à direita */}
            <div className="flex-1 flex items-center justify-end gap-3">
              <div className="relative">
                <button
                  type="button"
                  onClick={() => {
                    setIsNotificationOpen(!isNotificationOpen);
                  }}
                  className="p-2 text-purple-600 hover:text-purple-700 transition-colors"
                  aria-label="Notificações"
                >
                  <FiBell className="w-6 h-6" />
                </button>
                <NotificationPanel
                  isOpen={isNotificationOpen}
                  onClose={() => {
                    setIsNotificationOpen(false);
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => {
                  void navigate('/logs');
                }}
                className="p-2 text-purple-600 hover:text-purple-700 transition-colors"
                aria-label="Configurações"
              >
                <FiSettings className="w-6 h-6" />
              </button>
              <button
                type="button"
                onClick={handleLogoutClick}
                className="flex items-center justify-center w-10 h-10 bg-purple-600 hover:bg-purple-700 text-white rounded-full transition-colors"
                aria-label="Sair"
              >
                <FiLogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Section - Navigation Tabs */}
      <div className="border-b border-neutral-200">
        <div className="max-w-screen-2xl mx-auto px-8 py-3">
          <nav className="flex justify-center gap-2">
            {tabs.map((tab) =>
              tab.disabled ? (
                <div
                  key={tab.path}
                  className="group relative flex items-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-neutral-400 cursor-not-allowed"
                >
                  <span className="relative">
                    {tab.icon}
                    <span className="absolute inset-0 flex items-center justify-center bg-neutral-100 rounded opacity-0 group-hover:opacity-100 transition-opacity">
                      <FiLock className="w-4 h-4 text-neutral-500" />
                    </span>
                  </span>
                  {tab.label}
                </div>
              ) : (
                <NavLink
                  key={tab.path}
                  to={tab.path}
                  className={({ isActive }) =>
                    clsx(
                      'flex items-center gap-2 px-6 py-3 rounded-lg text-base font-medium transition-colors',
                      isActive
                        ? 'bg-purple-100 text-purple-600'
                        : 'text-neutral-600 hover:bg-neutral-100'
                    )
                  }
                >
                  {tab.icon}
                  {tab.label}
                </NavLink>
              )
            )}
          </nav>
        </div>
      </div>

      {/* Logout Confirmation Modal */}
      <LogoutModal
        isOpen={isLogoutModalOpen}
        onClose={handleLogoutModalClose}
        onConfirm={handleLogoutConfirm}
        isLoading={logoutMutation.isPending}
      />
    </header>
  );
};
